// Studio video statuses — one explicit state machine, one status per video, nothing implicit.
//
//   uploading ──uploaded──▶ ready-edit ──doneEditing──▶ ready-export ──startExport──▶ exporting ──exportOk──────▶ exported
//                              ▲  ▲                          │                            │  │                       │
//                              │  └──────backToEditing───────┘                            │  └─exportPartial─▶ partial ──retryFailed──▶ exporting
//                              │                                                      exportFail                    │
//                              │                                                          ▼                         │
//                              └────────────── reedit ─────────────────────────── failed ──retry──▶ exporting        │
//                              └────────────── reedit (from exported, partial, failed) ─────────────────────────────┘
//
// The four user-facing statuses (brief): Uploading (grey), Ready to edit (blue), Ready to export (orange), Exported (green).
// `exporting`, `partial` and `failed` only exist inside the bottom bar, between "the card landed" and "the export finished":
// they are what keeps a failed or incomplete export from ever looking exported, and a card from ever vanishing.
//   green (exported) = every variant came out · amber (partial) = some did, "Retry failed only" re-runs the missing ones
//   red (failed)     = nothing came out, full Retry
//
// Where a video lives is a function of its status, never stored separately:
//   queue (the workspace)      : uploading, ready-edit, ready-export
//   bottom bar (exported zone) : exporting, exported, partial, failed

export const STATUS = {
  UPLOADING: 'uploading',
  READY_EDIT: 'ready-edit',
  READY_EXPORT: 'ready-export',
  EXPORTING: 'exporting',
  EXPORTED: 'exported',
  PARTIAL: 'partial',
  FAILED: 'failed',
};

export const TRANSITIONS = {
  'uploading':    { uploaded: 'ready-edit' },
  'ready-edit':   { doneEditing: 'ready-export' },
  'ready-export': { backToEditing: 'ready-edit', startExport: 'exporting' },
  'exporting':    { exportOk: 'exported', exportPartial: 'partial', exportFail: 'failed' },
  'partial':      { retryFailed: 'exporting', reedit: 'ready-edit' },
  'failed':       { retry: 'exporting', reedit: 'ready-edit' },
  'exported':     { reedit: 'ready-edit' },
};

// Next status, or null when the event is not allowed from this status (callers keep the current status then).
export function transition(status, event) {
  const row = TRANSITIONS[status];
  return (row && row[event]) || null;
}

export const QUEUE_STATUSES = ['uploading', 'ready-edit', 'ready-export'];
export const BAR_STATUSES = ['exporting', 'exported', 'partial', 'failed'];
export const inQueue = (status) => QUEUE_STATUSES.includes(status);
export const inBar = (status) => BAR_STATUSES.includes(status);

// One colour per status, used everywhere it appears (queue dot, badge, card strip).
export const STATUS_LABEL = {
  'uploading': 'Uploading',
  'ready-edit': 'Ready to edit',
  'ready-export': 'Ready to export',
  'exporting': 'Exporting',
  'exported': 'Exported',
  'partial': 'Partly exported',
  'failed': 'Export failed',
};
export const STATUS_COLOR = {
  'uploading': '#9a9a9a',      // grey
  'ready-edit': '#3b7ddd',     // blue
  'ready-export': '#FF7900',   // orange
  'exporting': '#FF7900',      // orange while it runs
  'exported': '#1a7f4b',       // green: only for a fully successful export
  'partial': '#d98a00',        // amber: some variants out, the rest to retry
  'failed': '#c0392b',         // red
};

// The banner "Ready to export all your videos" shows only when the WHOLE queue is waiting for the go-ahead.
// Videos whose upload failed can't be exported and are ignored here (the user removes them).
export function allQueueReady(items) {
  const q = items.filter(v => v && inQueue(v.studio) && v.status !== 'error');
  return q.length > 0 && q.every(v => v.studio === 'ready-export');
}

// A snapshot is the set of edit settings a video was marked done with: [name, value, setter] entries in, plain object out.
// Re-editing restores it, so nothing the user set is lost.
export const takeSnapshot = (entries) => Object.fromEntries(entries.map(([name, value]) => [name, value]));
export function applySnapshot(entries, snapshot) {
  if (!snapshot) return 0;
  let n = 0;
  entries.forEach(([name, , setter]) => { if (Object.prototype.hasOwnProperty.call(snapshot, name) && setter) { setter(snapshot[name]); n++; } });
  return n;
}

// What a finished export means. generate() resolves even when it produced nothing (missing key, no voice…), so "it resolved"
// is never success. It reports { ok, failed, total } = variants produced / failed / expected:
//   everything expected is out           → exportOk      (green)
//   some out, some missing or failed     → exportPartial (amber, "2 of 3", Retry failed only)
//   nothing out                          → exportFail    (red, full Retry)
export const outcomeEvent = (res) => {
  if (!res || !(res.ok > 0)) return 'exportFail';
  return (res.failed > 0 || res.ok < (res.total || 0)) ? 'exportPartial' : 'exportOk';
};
// A run that did nothing: used for the early exits (no script, no API key, no voice…) so they are failures, not silence.
export const emptyRun = (failed = 0) => ({ ok: 0, failed, total: failed, okKeys: [], failedKeys: [] });
// "2 of 3" for the card of a partial export.
export const partialLabel = (okCount, total) => `${okCount} of ${total}`;
// Merge a "Retry failed only" run into what the video had already produced: the good variants stay, the new ones are added.
export function mergeRun(prev, next) {
  const okKeys = [...new Set([...(prev ? prev.okKeys : []), ...next.okKeys])];
  const total = prev ? prev.total : next.total;
  return { ok: okKeys.length, failed: next.failedKeys.length, total, okKeys, failedKeys: next.failedKeys };
}
